import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react';
import axios from 'axios';
import { generateErrorMsg, sendNotification } from 'utils/helperMethods';
import { customGroupsUrls } from 'config/urls';
import { queryClient } from 'services/reactQueryService';
import { ErrorMessages } from 'utils/Enums';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import useFilterState from './useFilterState';
import { CREATE_NEW_GROUP_VALUE, CUSTOM_GROUPS_QUERY_KEY } from '../constants';
import type { CustomProductGroupRecord, Dim1Option, GroupOption } from '../types';

interface FilterKey {
    label: string;
    value: string;
    dimension: string;
}

interface UseCustomProductGroupModalProps {
    modalFilterKeys: FilterKey[];
    onMutationSuccess?: () => void;
}

/** Adds the selected product_category to filters */
function mergeProductCategoryIntoFilters(
    filters: Record<string, { inclusion?: any[]; exclusion?: any[] }> | undefined,
    productCategory: string | undefined,
): Record<string, { inclusion: any[]; exclusion: any[] }> {
    const base = filters && typeof filters === 'object' ? { ...filters } : {};
    const dim1 = base.dim1 ?? { inclusion: [], exclusion: [] };
    const inclusion = Array.isArray(dim1.inclusion) ? [...dim1.inclusion] : [];
    const exclusion = Array.isArray(dim1.exclusion) ? [...dim1.exclusion] : [];
    if (productCategory) {
        inclusion.unshift({
            attributeType: 'product_category',
            attributeExId: productCategory,
            attributeDesc: productCategory,
            dimension: 'dim1',
        });
    }
    return { ...base, dim1: { inclusion, exclusion } };
}

function useCustomProductGroupModal({ modalFilterKeys, onMutationSuccess }: UseCustomProductGroupModalProps) {
    const modalFilter = useFilterState(modalFilterKeys);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<CustomProductGroupRecord | null>(null);

    const [selectedDim2Id, setSelectedDim2Id] = useState<number | undefined>();
    const [customerGroups, setCustomerGroups] = useState<GroupOption[]>([]);
    const [productCategories, setProductCategories] = useState<string[]>([]);
    const [selectedProductCategory, setSelectedProductCategory] = useState<string | undefined>();
    const [selectedGroupValue, setSelectedGroupValue] = useState<string | undefined>();

    const [newGroupName, setNewGroupName] = useState('');

    const [selectedItems, setSelectedItems] = useState<Dim1Option[]>([]); // left table
    const [dim1List, setDim1List] = useState<Dim1Option[]>([]); // right table (available)
    const [groupChangeDim1Loading, setGroupChangeDim1Loading] = useState(false);
    const [selectedItemCheckedKeys, setSelectedItemCheckedKeys] = useState<Key[]>([]);
    const [availableItemCheckedKeys, setAvailableItemCheckedKeys] = useState<Key[]>([]);

    const [upsertLoading, setUpsertLoading] = useState(false);

    // all selected items show as checked in the UI
    useEffect(() => {
        setSelectedItemCheckedKeys(selectedItems.map((item) => item.dim1_id));
    }, [selectedItems]);

    const mfFilterChangeCount = useRef(0);
    // Always-fresh ref for selectedItems so fetchDim1Options can exclude them without stale closure
    const selectedItemsRef = useRef<Dim1Option[]>([]);
    selectedItemsRef.current = selectedItems;

    const isCreatingNew = selectedGroupValue === CREATE_NEW_GROUP_VALUE;
    const showGroupFields = !!selectedGroupValue;

    // Product categories: only needed in add mode; in edit mode the product_category comes from the group-detail API
    const { loading: productCategoriesApiLoading, data: productCategoriesData } = useCommonApiHandler({
        method: 'post',
        url: customGroupsUrls.postProductCategoriesUrl,
        queryKey: ['custom-groups-product-categories', selectedDim2Id],
        variables: { dim2_id: selectedDim2Id },
        parserFunction: (res: any) => (Array.isArray(res) ? res : []),
        queryConfig: { enabled: isModalOpen && !!selectedDim2Id && !editingRecord },
        onError: () => sendNotification('error', 'Failed to load product categories.'),
    });

    // List groups: only in add mode; in edit mode the group is already known and the dropdown is disabled
    const listGroupsUrl =
        selectedDim2Id != null && selectedProductCategory && !editingRecord
            ? customGroupsUrls.listGroupsUrl(selectedDim2Id, selectedProductCategory)
            : '';
    const { loading: listGroupsApiLoading, data: listGroupsData } = useCommonApiHandler({
        method: 'get',
        url: listGroupsUrl,
        queryKey: ['custom-groups-list', selectedDim2Id, selectedProductCategory],
        parserFunction: (res: any) => (Array.isArray(res) ? res : res?.groups ?? []),
        queryConfig: { enabled: isModalOpen && !!selectedDim2Id && !!selectedProductCategory && !editingRecord },
        onError: () => sendNotification('error', 'Failed to load groups.'),
    });

    const editGroupDetailUrl = editingRecord ? customGroupsUrls.getGroupUrl(editingRecord.group_id) : '';
    const { loading: editGroupDetailLoading, data: editGroupDetailData } = useCommonApiHandler({
        method: 'get',
        url: editGroupDetailUrl,
        queryKey: ['custom-group-edit-detail', editingRecord?.group_id],
        parserFunction: (res: any) => res,
        queryConfig: { enabled: isModalOpen && !!editingRecord },
        onError: () => sendNotification('error', 'Failed to load group details.'),
    });

    const { loading: editDim1ApiLoading, data: editDim1Data } = useCommonApiHandler({
        method: 'post',
        url: customGroupsUrls.postDim1OptionsUrl,
        queryKey: ['custom-group-edit-dim1', editingRecord?.group_id, selectedProductCategory],
        variables:
            editingRecord != null && selectedProductCategory != null
                ? {
                      dim2_id: editingRecord.dim2_id,
                      limit: 1000,
                      offset: 0,
                      group_id: editingRecord.group_id,
                      filters: mergeProductCategoryIntoFilters({}, selectedProductCategory),
                      search_key: '',
                  }
                : {},
        parserFunction: (res: any) => res?.options ?? [],
        queryConfig: { enabled: isModalOpen && !!editingRecord && !!selectedProductCategory },
        onError: () => sendNotification('error', 'Failed to load group details.'),
    });

    const groupSelectOptions = useMemo(() => {
        const createOption = { label: '+ Create New Group', value: CREATE_NEW_GROUP_VALUE };
        const existingOptions = customerGroups.map((g) => ({ label: g.group_name, value: String(g.group_id) }));
        if (editingRecord && editGroupDetailData) {
            const name = (editGroupDetailData as any)?.group_name;
            if (name != null) {
                const currentOption = { label: name, value: String(editingRecord.group_id) };
                const exists = existingOptions.some((o) => o.value === currentOption.value);
                if (!exists) return [createOption, currentOption, ...existingOptions];
            }
        }
        return [createOption, ...existingOptions];
    }, [customerGroups, editingRecord, editGroupDetailData]);

    // Sync product categories from API to state
    useEffect(() => {
        if (selectedDim2Id != null && productCategoriesData != null) {
            setProductCategories(productCategoriesData);
        }
    }, [selectedDim2Id, productCategoriesData]);

    // When product category changes, clear only selection and tables
    useEffect(() => {
        if (selectedProductCategory == null) return;
        setSelectedGroupValue(undefined);
        setNewGroupName('');
        setSelectedItems([]);
        setDim1List([]);
        setSelectedItemCheckedKeys([]);
        setAvailableItemCheckedKeys([]);
    }, [selectedProductCategory]);

    // Sync list groups from API to state
    useEffect(() => {
        if (editingRecord) return;
        if (!selectedDim2Id || !selectedProductCategory) {
            setCustomerGroups([]);
            setSelectedGroupValue(undefined);
            setNewGroupName('');
            return;
        }
        if (listGroupsData != null) setCustomerGroups(listGroupsData);
    }, [editingRecord, selectedDim2Id, selectedProductCategory, listGroupsData]);

    // Set selectedProductCategory only when group detail is for the current record
    useEffect(() => {
        if (!editingRecord || !editGroupDetailData) return;
        const data = editGroupDetailData as any;
        if (data?.group_id != null && data.group_id !== editingRecord.group_id) return;
        const productCategory = data?.product_category;
        if (productCategory) setSelectedProductCategory(productCategory);
    }, [editingRecord, editGroupDetailData]);

    // Set group name, selected items, and dim1 list from group detail + dim1 options
    useEffect(() => {
        if (!editingRecord || !editGroupDetailData || !editDim1Data) return;
        const groupData = editGroupDetailData as any;
        setSelectedGroupValue(String(editingRecord.group_id));
        setNewGroupName(groupData?.group_name ?? '');
        const allOptions: Dim1Option[] = editDim1Data;
        const selectedIds = new Set((groupData?.dim1_ids ?? []).map(Number));
        setSelectedItems(allOptions.filter((item) => selectedIds.has(item.dim1_id)));
        setDim1List(allOptions.filter((item) => !selectedIds.has(item.dim1_id)));
    }, [editingRecord, editGroupDetailData, editDim1Data]);

    // Fetches available (right) items, excluding already-selected items
    const fetchDim1Options = useCallback(
        async (groupId?: number, filters: any = {}) => {
            setGroupChangeDim1Loading(true);
            setDim1List([]);
            try {
                const filtersWithProductCategory = mergeProductCategoryIntoFilters(filters, selectedProductCategory);
                const { data } = await axios.post<{ options: Dim1Option[]; total: number }>(
                    customGroupsUrls.postDim1OptionsUrl,
                    {
                        dim2_id: selectedDim2Id,
                        limit: 1000,
                        offset: 0,
                        group_id: groupId ?? 0,
                        filters: filtersWithProductCategory,
                        search_key: '',
                    },
                );
                const allOptions = data?.options ?? [];
                const selectedIds = new Set(selectedItemsRef.current.map((i) => i.dim1_id));
                setDim1List(allOptions.filter((item) => !selectedIds.has(item.dim1_id)));
            } catch {
                sendNotification('error', 'Failed to load material groups.');
            } finally {
                setGroupChangeDim1Loading(false);
            }
        },
        [selectedDim2Id, selectedProductCategory],
    );

    // Re-fetch available items when modal filters change
    useEffect(() => {
        if (!isModalOpen || selectedDim2Id == null || !showGroupFields) return;
        mfFilterChangeCount.current += 1;
        if (mfFilterChangeCount.current <= 1) return;
        const groupId = !isCreatingNew && selectedGroupValue ? Number(selectedGroupValue) : undefined;
        fetchDim1Options(isCreatingNew ? undefined : groupId, modalFilter.currentFilters);
    }, [modalFilter.currentFilters]);

    const resetModalState = useCallback(() => {
        setSelectedDim2Id(undefined);
        setCustomerGroups([]);
        setProductCategories([]);
        setSelectedProductCategory(undefined);
        setSelectedGroupValue(undefined);
        setNewGroupName('');
        setSelectedItems([]);
        setDim1List([]);
        setSelectedItemCheckedKeys([]);
        setAvailableItemCheckedKeys([]);
        modalFilter.reset();
        mfFilterChangeCount.current = 0;
    }, [modalFilter.reset]);

    const openAddModal = useCallback(() => {
        setEditingRecord(null);
        resetModalState();
        setIsModalOpen(true);
    }, [resetModalState]);

    const openEditModal = useCallback(
        (record: CustomProductGroupRecord) => {
            setEditingRecord(record);
            resetModalState();
            setIsModalOpen(true);
            setSelectedDim2Id(record.dim2_id);
        },
        [resetModalState],
    );

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setEditingRecord(null);
        resetModalState();
        queryClient.removeQueries({ queryKey: ['custom-group-edit-detail'], exact: false });
        queryClient.removeQueries({ queryKey: ['custom-group-edit-dim1'], exact: false });
    }, [resetModalState]);

    const handleCustomerChange = useCallback((dim2Id: any, _option: any) => {
        setSelectedDim2Id(dim2Id as number);
        setSelectedGroupValue(undefined);
        setSelectedItems([]);
        setDim1List([]);
        setSelectedItemCheckedKeys([]);
        setAvailableItemCheckedKeys([]);
        setNewGroupName('');
        setCustomerGroups([]);
        setProductCategories([]);
        setSelectedProductCategory(undefined);
    }, []);

    const handleGroupChange = useCallback(
        async (val: any) => {
            setSelectedGroupValue(val as string);
            setSelectedItems([]);
            setDim1List([]);
            setSelectedItemCheckedKeys([]);
            setAvailableItemCheckedKeys([]);
            setNewGroupName('');
            modalFilter.reset();
            mfFilterChangeCount.current = 0;
            if (val === CREATE_NEW_GROUP_VALUE) {
                fetchDim1Options(undefined, {});
            } else {
                const group = customerGroups.find((g) => String(g.group_id) === val);
                if (!group) return;
                setGroupChangeDim1Loading(true);
                try {
                    const [groupRes, dim1Res] = await Promise.all([
                        axios.get(customGroupsUrls.getGroupUrl(group.group_id)),
                        axios.post<{ options: Dim1Option[]; total: number }>(customGroupsUrls.postDim1OptionsUrl, {
                            dim2_id: selectedDim2Id,
                            limit: 1000,
                            offset: 0,
                            group_id: group.group_id,
                            filters: mergeProductCategoryIntoFilters({}, selectedProductCategory),
                            search_key: '',
                        }),
                    ]);
                    setNewGroupName(groupRes.data?.group_name ?? '');
                    const allOptions: Dim1Option[] = dim1Res.data?.options ?? [];
                    const selectedIds = new Set((groupRes.data?.dim1_ids ?? []).map(Number));
                    setSelectedItems(allOptions.filter((item) => selectedIds.has(item.dim1_id)));
                    setDim1List(allOptions.filter((item) => !selectedIds.has(item.dim1_id)));
                } catch {
                    sendNotification('error', 'Failed to load group details.');
                } finally {
                    setGroupChangeDim1Loading(false);
                }
            }
        },
        [customerGroups, selectedDim2Id, selectedProductCategory, modalFilter.reset, fetchDim1Options],
    );

    // Move checked available items → selected
    const moveToSelected = useCallback(() => {
        const toMove = dim1List.filter((item) => availableItemCheckedKeys.includes(item.dim1_id));
        setSelectedItems((prev) => [...prev, ...toMove]);
        setDim1List((prev) => prev.filter((item) => !availableItemCheckedKeys.includes(item.dim1_id)));
        setAvailableItemCheckedKeys([]);
    }, [dim1List, availableItemCheckedKeys]);

    // Move checked selected items → available
    const moveToAvailable = useCallback(() => {
        const toMove = selectedItems.filter((item) => selectedItemCheckedKeys.includes(item.dim1_id));
        setDim1List((prev) => [...prev, ...toMove]);
        setSelectedItems((prev) => prev.filter((item) => !selectedItemCheckedKeys.includes(item.dim1_id)));
        setSelectedItemCheckedKeys([]);
    }, [selectedItems, selectedItemCheckedKeys]);

    const handleSave = useCallback(async () => {
        // Save button is disabled unless customer, group, new group name (when creating), and at least one material group are set
        const userEmail = localStorage.getItem('email') || '';
        const payload = isCreatingNew
            ? {
                  dim2_id: selectedDim2Id,
                  group_name: newGroupName.trim(),
                  product_category: selectedProductCategory,
                  dim1_ids: selectedItems.map((i) => i.dim1_id),
                  user_email: userEmail,
              }
            : {
                  group_name: newGroupName.trim(),
                  product_category: selectedProductCategory,
                  dim1_ids: selectedItems.map((i) => i.dim1_id),
                  user_email: userEmail,
              };

        const url = isCreatingNew
            ? customGroupsUrls.createGroupUrl
            : customGroupsUrls.updateGroupUrl(Number(selectedGroupValue));

        setUpsertLoading(true);
        try {
            if (isCreatingNew) {
                await axios.post(url, payload);
            } else {
                await axios.patch(url, payload);
            }
            sendNotification('success', isCreatingNew ? 'Group created successfully.' : 'Group updated successfully.');
            queryClient.removeQueries({ queryKey: [CUSTOM_GROUPS_QUERY_KEY], exact: false });
            queryClient.removeQueries({ queryKey: ['custom-groups-list'], exact: false });
            onMutationSuccess?.();
            closeModal();
        } catch (error: any) {
            sendNotification(
                'error',
                generateErrorMsg(String(error?.response?.data?.detail || ''), ErrorMessages.GENERALIZED_ERROR_MSG),
            );
        } finally {
            setUpsertLoading(false);
        }
    }, [
        selectedDim2Id,
        selectedGroupValue,
        selectedProductCategory,
        isCreatingNew,
        newGroupName,
        selectedItems,
        closeModal,
        onMutationSuccess,
    ]);

    const customerGroupsLoading = listGroupsApiLoading;
    const productCategoriesLoading = productCategoriesApiLoading;
    const dim1Loading = editingRecord ? editDim1ApiLoading : groupChangeDim1Loading;
    // True while the edit modal is still fetching/applying data (group detail → selectedProductCategory → dim1 → selectedGroupValue)
    const editFlowLoading =
        !!editingRecord && (editGroupDetailLoading || editDim1ApiLoading || selectedGroupValue === undefined);

    return {
        modalFilter,
        isModalOpen,
        editingRecord,
        selectedDim2Id,
        customerGroups,
        customerGroupsLoading,
        productCategories,
        productCategoriesLoading,
        selectedProductCategory,
        setSelectedProductCategory,
        selectedGroupValue,
        groupSelectOptions,
        newGroupName,
        setNewGroupName,
        selectedItems,
        dim1List,
        dim1Loading,
        selectedItemCheckedKeys,
        setSelectedItemCheckedKeys,
        availableItemCheckedKeys,
        setAvailableItemCheckedKeys,
        moveToSelected,
        moveToAvailable,
        upsertLoading,
        isCreatingNew,
        showGroupFields,
        editFlowLoading,
        openAddModal,
        openEditModal,
        closeModal,
        handleCustomerChange,
        handleGroupChange,
        handleSave,
    };
}

export default useCustomProductGroupModal;
